import { memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import "react-datepicker/dist/react-datepicker.css";
import { Link, useNavigate } from 'react-router-dom';
import { getApiListingsDrafts, ListingDraft, postApiListingsPublish, PublishRequest } from '../../api/listings-client';
import logo from '../../resources/logo.svg';
import LoadingSpinner from '../LoadingSpinner';
import AccommodationTypeStep from './CreateListing/AccomodationStep';
import AddressStep from './CreateListing/AddressStep';
import AnimatedStepContainer from './CreateListing/AnimatedStepContainer';
import { DescriptionStep } from './CreateListing/DescriptionStep';
import FacilitiesStep from './CreateListing/FacilitiesStep';
import { useImageArranger } from './CreateListing/ImagesArranger';
import PricingStep from './CreateListing/PricingStep';
import { SaveDraftQueue } from './CreateListing/SaveDraftQueue';
import TimeStep from './CreateListing/TimeStep';
import ToastError from './CreateListing/ToastError';
import { ImageData } from './CreateListing/Types'; // Import the ImageData interface
import './customdatepicker.css';
import { useImagesModalContent } from './Listing/EditModal';

const SubmissionSuccess = React.lazy(() => import('./Listing/SubmissionSuccess'));

const MAX_STEP_COUNT = 7;

const ImagesStep = memo(({ onUpdate, initialImages }: {
    onUpdate: (images: ImageData[]) => void,
    initialImages: ImageData[]
}) => {
    const {
        draggingId,
        boxStates,
        handleImageUpload,
        handleDragStart,
        handleDelete,
        containerStyle,
        setIsMounted,
        containerRef,
        enableTransitions,
        resetArrangement,
    } = useImageArranger({
        initialImages,  // Use the prop here
        onImagesUpdate: onUpdate
    });


    const imagesContent = useImagesModalContent({
        boxStates,
        draggingId,
        enableTransitions,
        handleDragStart,
        handleDelete,
        handleImageUpload,
        containerStyle,
        containerRef,
    });

    useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);

    return (
        <div className="py-24">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-2xl font-bold text-gray-800 sm:text-4xl mb-6 pointer-events-none text-center">Upload Images</h1>
                <p className="text-lg text-gray-800 dark:text-neutral-400 mb-12 pointer-events-none text-center">
                    <span className="hidden sm:inline">Drag the images to rearrange the order they'll show on the listing.</span>
                    <span className="sm:hidden">Hold and drag the images to rearrange the order they'll show on the listing.</span>
                </p>
                <div ref={containerRef} className="w-full">
                    {imagesContent}
                </div>
            </div>
        </div>
    );
});

const CreateListing = () => {
    const [draftData, setDraftData] = useState<ListingDraft>({});
    const [images, setImages] = useState<ImageData[]>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [isAddressValid, setIsAddressValid] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const navigate = useNavigate();

    const saveDraftQueueRef = useRef(new SaveDraftQueue());

    const updateDraft = (updates: Partial<ListingDraft>) => {
        setDraftData(prev => ({ ...prev, ...updates }));
    };

    const loadExistingImages = useCallback(async (draft: ListingDraft) => {
        if (draft.image_media_ids && draft.image_url_cache) {
            const newImages = draft.image_media_ids.map((mediaId, index) => ({
                id: mediaId,
                image: draft.image_url_cache![index],
                mediaId: mediaId
            }));
            setImages(newImages);
        }
    }, []);

    const initialImages = useMemo(() => {
        console.log("Initial images");
        if (draftData.image_media_ids && draftData.image_url_cache) {
            return draftData.image_media_ids.map((mediaId, index) => ({
                id: mediaId,
                image: draftData.image_url_cache![index],
                mediaId: mediaId
            }));
        }
        return [];
    }, [draftData.image_media_ids, draftData.image_url_cache]);

    const onImagesUpdate = useCallback((newImages: ImageData[]) => {
        if (newImages.length > 0 && newImages[0].mediaId) {
            setImages(newImages);
            updateDraft({
                image_media_ids: newImages.map(img => img.mediaId as string),
                image_url_cache: newImages.map(img => img.image)
            });
        }
    }, []);


    useEffect(() => {
        const fetchDraft = async () => {
            try {
                setIsLoading(true);
                const drafts = await getApiListingsDrafts();
                if (drafts.length > 0) {
                    setDraftData(drafts[0]);
                    await loadExistingImages(drafts[0]);
                    const nextIncompleteStep = determineNextIncompleteStep(drafts[0]);
                    setCurrentStep(nextIncompleteStep);
                } else {
                    setCurrentStep(1);
                }
            } catch (error) {
                console.error('Error fetching drafts:', error);
                setError('Failed to fetch draft. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchDraft();
    }, [loadExistingImages]);

    const fileToBase64 = (file: File | Blob): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });
    };

    const downloadAndConvertImage = async (url: string, index: number) => {
        try {
            // Construct the full URL
            const fullUrl = url.startsWith('http') ? url : `https://${url}`;

            const response = await fetch(fullUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const blob = await response.blob();
            const base64 = await fileToBase64(blob);
            setImages(prevImages => prevImages.map((img, i) =>
                i === index ? { ...img, preview: base64, isDownloading: false } : img
            ));
        } catch (error) {
            console.error('Error downloading image:', error);
            setImages(prevImages => prevImages.map((img, i) =>
                i === index ? { ...img, uploadError: 'Failed to download', isDownloading: false } : img
            ));
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            // Wait for any pending draft saves to complete
            await new Promise<void>((resolve) => {
                const checkQueue = () => {
                    if (!saveDraftQueueRef.current.getIsProcessing()) {
                        resolve();
                    } else {
                        setTimeout(checkQueue, 100);
                    }
                };
                checkQueue();
            });

            const finalDraftId = saveDraftQueueRef.current.getLastSavedId() || draftData.id;

            if (!finalDraftId) {
                throw new Error('No draft ID available for submission');
            }

            function ensureValueAllowUndefined<T>(value: T | null | undefined, fieldName: string): T | undefined {
                if (value === null) {
                    throw new Error(`Field ${fieldName} cannot be null.`);
                }
                return value;
            }

            function ensureValue<T>(value: T | null | undefined, fieldName: string): T {
                if (value === null || value === undefined) {
                    throw new Error(`Field ${fieldName} is required and cannot be null or undefined.`);
                }
                return value;
            }
            // Prepare the request body
            const publishReq: PublishRequest = {
                draftId: finalDraftId,
                roomType: ensureValue(draftData.roomType, 'roomType'),
                bathroom: ensureValue(draftData.bathroom, 'bathroom'),
                furnished: ensureValue(draftData.furnished, 'furnished'),
                parking: ensureValue(draftData.parking, 'parking'),
                rent: ensureValue(draftData.rent, 'rent'),
                bond: ensureValue(draftData.bond, 'bond'),
                billsIncluded: ensureValue(draftData.billsIncluded, 'billsIncluded'),
                availableFrom: ensureValue(draftData.availableFrom, 'availableFrom'),
                minimumStay: ensureValue(draftData.minimumStay, 'minimumStay'),
                maximumStay: ensureValue(draftData.maximumStay, 'maximumStay'),
                localityId: ensureValue(draftData.localityId, 'localityId'),
                streetAddress: ensureValue(draftData.addressName, 'streetAddress'),
                addressId: ensureValueAllowUndefined(draftData.addressId, 'addressId'),
                image_media_ids: ensureValue(draftData.image_media_ids, 'image_media_ids'),
                description: ensureValue(draftData.description, 'description')
            };

            try {
                const response = await postApiListingsPublish({ requestBody: publishReq });
                if (response.errorMessage && response.errorMessage.trim().length > 0) {
                    setError(response.errorMessage);
                } else {
                    // Show success modal instead of navigating immediately
                    setShowSuccessModal(true);
                }
            } catch (apiError: any) {
                console.error('API Error during submission:', apiError);
                // Check if the error has a response property (like axios errors)
                if (apiError.response) {
                    console.error('API Error status:', apiError.response.status);
                    console.error('API Error data:', apiError.response.data);
                    setError(`Server error: ${apiError.response.status} - ${apiError.response.data?.message || 'Unknown error'}`);
                } else if (apiError.message) {
                    setError(`Error: ${apiError.message}`);
                } else {
                    setError('Failed to submit listing. Server returned an error.');
                }
            }
        } catch (error: any) {
            console.error('Error during submission:', error);
            // Only set a generic error if one wasn't already set
            if (!error.handled) {
                setError('Failed to submit listing. Please check all fields and try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isNextButtonDisabled = () => {
        return !isStepComplete() || isSaving || isSubmitting;
    };

    useEffect(() => {
        return () => {
            images.forEach(image => {
                if (image.preview) {
                    URL.revokeObjectURL(image.preview);
                }
            });
        };
    }, []);

    const determineNextIncompleteStep = (draft: ListingDraft): number => {
        if (draft.roomType == null) return 1;
        if (draft.bathroom == null || draft.furnished == null || draft.parking == null) return 2;
        if (draft.rent == null || draft.bond == null || draft.billsIncluded == null) return 3;
        if (draft.availableFrom == null || draft.minimumStay == null || draft.maximumStay == null) return 4;
        if (draft.addressId == null) return 5;
        if (draft.image_media_ids == null || draft.image_media_ids.length === 0) return 6;
        return MAX_STEP_COUNT; // All steps are complete, start from the end
    };

    const saveDraft = () => {
        saveDraftQueueRef.current.enqueueSaveDraft(draftData, (success) => {
            if (!success) {
                console.error('Failed to save draft in background');
            }
        });
    };

    const nextStep = async () => {
        if (currentStep < MAX_STEP_COUNT && isStepComplete() && !isSaving) {
            setCurrentStep(currentStep + 1);
            saveDraft();
        }
    };

    const prevStep = () => {
        console.log(draftData);
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const isStepComplete = (): boolean => {
        switch (currentStep) {
            case 1:
                return draftData.roomType != null;
            case 2:
                return draftData.bathroom != null && draftData.furnished != null && draftData.parking != null;
            case 3:
                return draftData.rent != null && draftData.bond != null && draftData.billsIncluded != null;
            case 4:
                return draftData.availableFrom != null && draftData.minimumStay != null && draftData.maximumStay != null;
            case 5:
                return draftData.addressId != null && isAddressValid;
            case 6:
                return draftData.image_media_ids != null &&
                    draftData.image_media_ids.length > 0 &&
                    images.every(img => img.mediaId); // Simplified check for valid images
            case 7:
                return draftData.description != null && draftData.description.length >= 30;
            default:
                return false;
        }
    };

    const handleSaveAndExit = async () => {
        setIsSaving(true);
        setError(null);
        try {
            const success = await new Promise<boolean>((resolve) => {
                saveDraftQueueRef.current.enqueueSaveDraft(draftData, resolve);
            });
            if (success) {
                navigate('/');
            } else {
                setError('Failed to save draft. Please try again later.');
            }
        } catch (error) {
            console.error('Error during save and exit:', error);
            setError('An unexpected error occurred. Please try again later.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderStep = () => {
        if (isLoading || isSaving || isSubmitting) {
            return (
                <LoadingSpinner />
            );
        }
        switch (currentStep) {
            case 1:
                return (
                    <AccommodationTypeStep
                        roomType={draftData.roomType}
                        setRoomType={(type) => updateDraft({ roomType: type })}
                    />
                );
            case 2:
                return (
                    <div className="py-24">
                        <FacilitiesStep
                            parking={draftData.parking}
                            setParking={(value: boolean) => updateDraft({ parking: value })}
                            bathrooms={
                                draftData.bathroom
                            }
                            setBathrooms={(value: string) => updateDraft({ bathroom: value })}
                            furnished={draftData.furnished}
                            setFurnished={(value: boolean) => updateDraft({ furnished: value })}
                        />
                    </div>
                );
            case 3:
                return (
                    <div className="py-24">
                        <PricingStep
                            rent={draftData.rent}
                            setRent={(value: number) => updateDraft({ rent: value })}
                            bond={draftData.bond}
                            setBond={(value: number) => updateDraft({ bond: value })}
                            billsIncluded={draftData.billsIncluded}
                            setBillsIncluded={(value: boolean) => updateDraft({ billsIncluded: value })}
                        />
                    </div>
                );
            case 4:
                return (
                    <div className="py-24">
                        <TimeStep
                            availableFrom={draftData.availableFrom}
                            setAvailableFrom={(value: string) => updateDraft({ availableFrom: value })}
                            minimumStay={draftData.minimumStay}
                            setMinimumStay={(value: string) => updateDraft({ minimumStay: value as MinimumStay })}
                            maximumStay={draftData.maximumStay}
                            setMaximumStay={(value: string) => updateDraft({ maximumStay: value as MaximumStay })}
                        />
                    </div>
                );
            case 5:
                return (
                    <AddressStep
                        onLocalitySelect={(localityId, localityName) => {
                            console.log("on locality select ", localityName, localityId);
                            updateDraft({
                                localityId,
                                localityName
                            });
                        }
                        }
                        onAddressValidityChange={setIsAddressValid}
                        onAddressSelect={(addressId, addressName, localityId) =>
                            updateDraft({
                                addressId,
                                addressName,
                                localityId
                            })
                        }
                        savedLocalityId={draftData.localityId}
                        savedLocalityName={draftData.localityName}
                        savedAddressId={draftData.addressId}
                        savedAddressName={draftData.addressName}
                    />
                );
            case 6:
                return <ImagesStep onUpdate={onImagesUpdate} initialImages={initialImages} />;
            case 7:
                return (
                    <DescriptionStep
                        savedDescription={draftData.description}
                        onDescriptionChange={(description: string) => updateDraft({ description: description })}
                        isDisabled={isSubmitting}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div>
            <div className="fixed top-0 w-full bg-white flex justify-between items-center px-12 py-8 z-50">
                <Link to="/" className="flex-none text-xl font-semibold" aria-label="Brand">
                    <img src={logo} alt="Website Logo" height="25" className="max-w-none h-8" />
                </Link>
                <button
                    type="button"
                    className="py-3 px-4 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-500 hover:border-[#24ccf2] hover:text-[#24ccf2] disabled:opacity-50 disabled:pointer-events-none transition-colors duration-200"
                    onClick={handleSaveAndExit}
                    disabled={currentStep === 0 || isSaving || isSubmitting}
                >
                    {isSaving ? (
                        <>
                            <LoadingSpinner />
                        </>
                    ) : (
                        'Save and exit'
                    )}
                </button>
            </div>

            <div className="flex items-center justify-center min-h-screen overflow-hidden">
                <div className="w-full text-center py-10 px-4 sm:px-6 lg:px-8">
                    <ToastError error={error} onDismiss={() => setError(null)} />
                    <AnimatedStepContainer step={currentStep}>
                        {renderStep()}
                    </AnimatedStepContainer>
                </div>
            </div>

            <div className="fixed bottom-0 w-full h-24 bg-white shadow-md z-50">
                <div className="px-4">
                    <div
                        className="w-full h-1.5 bg-gradient-to-r from-[#24ccf2]/10 to-[#2476f2]/10 rounded-full overflow-hidden"
                        role="progressbar"
                        aria-valuenow={(currentStep / MAX_STEP_COUNT) * 100}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        <div
                            className="h-full bg-gradient-to-r from-[#24ccf2] to-[#2476f2] transition-all duration-500 ease-in-out"
                            style={{
                                width: `${((currentStep - 1) / MAX_STEP_COUNT) * 110 + 5}%`,
                                // This ensures the gradient stays full-width and appears to "fill up" rather than stretch
                                backgroundSize: `${MAX_STEP_COUNT * 100}% 100%`,
                                backgroundPosition: 'left'
                            }}
                        />
                    </div>
                </div>

                <div className="flex justify-between items-center px-8 h-full pb-2">
                    <button
                        type="button"
                        className="py-3 px-4 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-500 hover:border-[#24ccf2] hover:text-[#24ccf2] disabled:opacity-50 disabled:pointer-events-none transition-colors duration-200"
                        onClick={prevStep}
                        disabled={currentStep <= 1 || isSaving || isSubmitting}
                    >
                        Previous
                    </button>

                    <button
                        type="button"
                        className={`py-3 ${currentStep === MAX_STEP_COUNT ? 'px-5' : 'px-7'} inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#24ccf2] to-[#2476f2] text-white hover:opacity-90 disabled:opacity-50 disabled:pointer-events-none transition-opacity duration-200`}
                        onClick={currentStep === MAX_STEP_COUNT ? handleSubmit : nextStep}
                        disabled={isNextButtonDisabled()}
                    >
                        {currentStep === MAX_STEP_COUNT ? (
                            isSubmitting ? (
                                <>
                                    Submitting...
                                </>
                            ) : (
                                'Submit'
                            )
                        ) : (
                            'Next'
                        )}
                    </button>
                </div>
            </div>

            {/* Success modal */}
            {showSuccessModal && (
                <Suspense fallback={
                    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center">
                        <div className="bg-white p-8 rounded-xl shadow-xl">
                            <LoadingSpinner />
                        </div>
                    </div>
                }>
                    <SubmissionSuccess
                        onClose={() => {
                            setShowSuccessModal(false);
                            navigate('/manage-listings');
                        }}
                    />
                </Suspense>
            )}
        </div>
    );
};

export default CreateListing;